import { ColumnDef } from '@tanstack/react-table'
import { TFunction } from 'i18next'
import { capitalize } from 'lodash'
import { Mail, Webhook } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { infoByType } from 'app/frontend/base/pages/UserDashboard/subpages/UserLinkedAccounts/components/LinkedAccount'
import { imageUrl } from 'app/frontend/base/helpers'
import DiscordChannelInfo from './DiscordChannelInfo'

type Params = {
  team: wowaudit.Team
  discordChannelDetails: wowaudit.DiscordChannelDetails[]
  t: TFunction
}

export const buildNotificationTemplateColumns = ({
  team,
  discordChannelDetails,
  t,
}: Params): ColumnDef<wowaudit.NotificationTemplate>[] => [
  {
    header: 'Type',
    cell: (cell) => {
      return (
        <span className='flex items-center gap-2 whitespace-nowrap'>
          <Badge variant='outline'>{cell.row.original.category}</Badge>
          {t(`notifications.types.${cell.row.original.key}.label`)}
        </span>
      )
    },
  },
  {
    header: 'Method',
    accessorKey: 'deliveryMethod',
    cell: (cell) => {
      return (
        <div className='flex items-center gap-2'>
          <Tooltip delayDuration={150}>
            <TooltipTrigger>
              {
                {
                  discord_message: (
                    <img
                      src={imageUrl(infoByType.Discord.logo)}
                      className='w-5'
                    />
                  ),
                  email: <Mail size={20} />,
                  custom_webhook: <Webhook size={20} />,
                }[cell.getValue() as keyof typeof cell.getValue]
              }
            </TooltipTrigger>
            <TooltipContent>
              {capitalize(String(cell.getValue()).replace('_', ' '))}
            </TooltipContent>
          </Tooltip>
        </div>
      )
    },
  },
  {
    header: 'Target',
    accessorKey: 'deliveryTarget',
    cell: (cell) => {
      if (cell.row.original.deliveryMethod === 'custom_webhook') {
        const targetAddress = cell.row.original.targetAddress ?? ''
        return URL.canParse(targetAddress) ? (
          new URL(targetAddress).hostname
        ) : (
          <span className='text-warning-foreground'>Invalid URL</span>
        )
      }

      if (cell.getValue() === 'user') {
        return (
          team.userReferences?.find(
            (user) => user.id === cell.row.original.deliveryTargetId,
          )?.obfuscatedBattletag ?? 'Hidden user'
        )
      }

      if (cell.getValue() === 'subject') {
        return <em className='opacity-70'>Subject</em>
      }

      if (cell.row.original.deliveryMethod === 'discord_message') {
        return (
          <DiscordChannelInfo
            address={cell.row.original.targetAddress}
            allDetails={discordChannelDetails ?? []}
          />
        )
      }

      return ''
    },
  },
]
